import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";

/**
 * RuleModal — Diálogo de creación/edición de reglas del Package Builder del catálogo.
 *
 * Extraído 1:1 desde `pages/CatalogPage.js` (modal más grande de la página).
 * Renderiza campos dinámicos según el tipo de regla seleccionado:
 *  - EXCLUSIVIDAD: filtra el segundo componente del paquete al seleccionar el primero.
 *  - REQUIERE_CATEGORIA: exige presencia de un producto de otra categoría.
 *  - DESCUENTO_CONDICIONAL: habilita un descuento solo si el producto requerido está presente.
 *  - BLOQUEO_STANDALONE: impide contratar un producto de forma independiente.
 *
 * @param {boolean}   props.ruleModalOpen     Controla la apertura para "Nueva Regla".
 * @param {Function}  props.setRuleModalOpen  Setter del flag de apertura.
 * @param {Object|null} props.editingRule     Regla en edición (null = modo creación).
 * @param {Function}  props.setEditingRule    Setter de la regla en edición.
 * @param {Object}    props.newRule           Estado del formulario para una regla nueva.
 * @param {Function}  props.setNewRule        Setter del formulario de regla nueva.
 * @param {Array}     props.services          Catálogo completo de servicios/productos.
 * @param {Array}     props.categories        Categorías disponibles.
 * @param {Array}     props.discountProducts  Productos de tipo descuento (precio_paquete < 0).
 * @param {Function}  props.formatCurrency    Formateador de moneda (EUR) del padre.
 * @param {Function}  props.resetNewRule      Restablece el formulario de regla nueva.
 * @param {Function}  props.handleCreateRule  Handler de creación de regla.
 * @param {Function}  props.handleUpdateRule  Handler de actualización de regla.
 */
export const RuleModal = ({
  ruleModalOpen,
  setRuleModalOpen,
  editingRule,
  setEditingRule,
  newRule,
  setNewRule,
  services,
  categories,
  discountProducts,
  formatCurrency,
  resetNewRule,
  handleCreateRule,
  handleUpdateRule,
}) => {
  return (
      <Dialog open={ruleModalOpen || !!editingRule} onOpenChange={(open) => {
        if (!open) {
          setRuleModalOpen(false);
          setEditingRule(null);
        }
      }}>
        <DialogContent className="bg-[#0F0F3D] border-[#1E1E5F] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">
              {editingRule ? "Editar Regla" : "Nueva Regla"}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const rule = editingRule || newRule;
            const setRule = editingRule ? setEditingRule : setNewRule;
            
            return (
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-300">Nombre de la regla *</Label>
                  <Input
                    value={rule.nombre}
                    onChange={(e) => setRule({ ...rule, nombre: e.target.value })}
                    placeholder="Ej: Fibra 300Mbps solo con Móvil 25GB"
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Tipo</Label>
                  <Select
                    value={rule.tipo}
                    onValueChange={(v) => setRule({ ...rule, tipo: v })}
                  >
                    <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                      <SelectItem value="EXCLUSIVIDAD" className="text-white">EXCLUSIVIDAD</SelectItem>
                      <SelectItem value="REQUIERE_CATEGORIA" className="text-white">REQUIERE_CATEGORIA</SelectItem>
                      <SelectItem value="DESCUENTO_CONDICIONAL" className="text-white">DESCUENTO_CONDICIONAL</SelectItem>
                      <SelectItem value="BLOQUEO_STANDALONE" className="text-white">BLOQUEO_STANDALONE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-300">Descripción</Label>
                  <Textarea
                    value={rule.descripcion || ""}
                    onChange={(e) => setRule({ ...rule, descripcion: e.target.value })}
                    placeholder="Descripción de la regla..."
                    rows={2}
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                </div>

                {/* Dynamic fields based on type */}
                <div className="border-t border-[#1E1E5F] pt-4">
                  {rule.tipo === "EXCLUSIVIDAD" && (
                    <div className="space-y-4">
                      <p className="text-xs text-slate-500 mb-2">
                        Cuando se seleccione el primer producto, el segundo campo del paquete quedará filtrado mostrando solo el producto requerido.
                      </p>
                      <div>
                        <Label className="text-slate-300">Si el comercial selecciona...</Label>
                        <Select
                          value={rule.condicion_producto_id || ""}
                          onValueChange={(v) => {
                            const product = services.find(s => s.id === v);
                            setRule({ 
                              ...rule, 
                              condicion_producto_id: v || null,
                              condicion_producto_nombre: product?.nombre || null
                            });
                          }}
                        >
                          <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                            <SelectValue placeholder="Selecciona producto" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                            {services.map((s) => (
                              <SelectItem key={s.id} value={s.id} className="text-white">
                                {s.nombre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">...el otro componente SOLO puede ser:</Label>
                        <Select
                          value={rule.producto_requerido_id || ""}
                          onValueChange={(v) => {
                            const product = services.find(s => s.id === v);
                            setRule({ 
                              ...rule, 
                              producto_requerido_id: v || null,
                              producto_requerido_nombre: product?.nombre || null
                            });
                          }}
                        >
                          <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                            <SelectValue placeholder="Selecciona producto requerido" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                            {services.map((s) => (
                              <SelectItem key={s.id} value={s.id} className="text-white">
                                {s.nombre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {rule.tipo === "REQUIERE_CATEGORIA" && (
                    <div className="space-y-4">
                      <p className="text-xs text-slate-500 mb-2">
                        El bloque de la primera categoría se deshabilitará si no hay ningún producto de la segunda categoría en el presupuesto.
                      </p>
                      <div>
                        <Label className="text-slate-300">Si se añade un producto de categoría...</Label>
                        <Select
                          value={rule.categoria_condicion || ""}
                          onValueChange={(v) => setRule({ ...rule, categoria_condicion: v || null })}
                        >
                          <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                            <SelectValue placeholder="Selecciona categoría" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                            {categories.map((cat) => (
                              <SelectItem key={cat.id || cat.name} value={cat.name} className="text-white">
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">...debe haber también un producto de categoría:</Label>
                        <Select
                          value={rule.categoria_requerida || ""}
                          onValueChange={(v) => setRule({ ...rule, categoria_requerida: v || null })}
                        >
                          <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                            <SelectValue placeholder="Selecciona categoría requerida" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                            {categories.map((cat) => (
                              <SelectItem key={cat.id || cat.name} value={cat.name} className="text-white">
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {rule.tipo === "DESCUENTO_CONDICIONAL" && (
                    <div className="space-y-4">
                      <p className="text-xs text-slate-500 mb-2">
                        El checkbox o dropdown de este descuento aparecerá deshabilitado hasta que el producto requerido esté en el presupuesto.
                      </p>
                      <div>
                        <Label className="text-slate-300">El descuento:</Label>
                        <Select
                          value={rule.condicion_producto_id || ""}
                          onValueChange={(v) => {
                            const product = services.find(s => s.id === v);
                            setRule({ 
                              ...rule, 
                              condicion_producto_id: v || null,
                              condicion_producto_nombre: product?.nombre || null
                            });
                          }}
                        >
                          <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                            <SelectValue placeholder="Selecciona descuento" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                            {discountProducts.map((s) => (
                              <SelectItem key={s.id} value={s.id} className="text-white">
                                {s.nombre} ({formatCurrency(s.precio_paquete)})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Solo se puede aplicar si está presente:</Label>
                        <Select
                          value={rule.producto_requerido_id || ""}
                          onValueChange={(v) => {
                            const product = services.find(s => s.id === v);
                            setRule({ 
                              ...rule, 
                              producto_requerido_id: v || null,
                              producto_requerido_nombre: product?.nombre || null
                            });
                          }}
                        >
                          <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                            <SelectValue placeholder="Selecciona producto" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                            {services.map((s) => (
                              <SelectItem key={s.id} value={s.id} className="text-white">
                                {s.nombre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Aplica a producto:</Label>
                        <p className="text-xs text-slate-500 mt-1">
                          Producto al que se aplica este descuento (Centralita 1x, WiFi 1AP, …). Requerido para descuentos de Centralita y WiFi en el Package Builder.
                        </p>
                        <Select
                          value={rule.producto_objetivo_id || "__none__"}
                          onValueChange={(v) => {
                            const val = v === "__none__" ? null : v;
                            const product = val ? services.find(s => s.id === val) : null;
                            setRule({
                              ...rule,
                              producto_objetivo_id: val,
                              producto_objetivo_nombre: product?.nombre || null,
                            });
                          }}
                        >
                          <SelectTrigger
                            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                            data-testid="rule-producto-objetivo-select"
                          >
                            <SelectValue placeholder="— Sin producto objetivo —" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                            <SelectItem value="__none__" className="text-slate-400">— Sin producto objetivo —</SelectItem>
                            {services.filter(s => (s.precio_paquete ?? s.precio_mensual ?? 0) >= 0).map((s) => (
                              <SelectItem key={s.id} value={s.id} className="text-white">
                                {s.nombre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {rule.tipo === "BLOQUEO_STANDALONE" && (
                    <div className="space-y-4">
                      <p className="text-xs text-slate-500 mb-2">
                        Este producto no aparecerá en el tab 'Servicios Individuales' del Pipeline, solo en el Constructor de Paquetes.
                      </p>
                      <div>
                        <Label className="text-slate-300">Producto que nunca puede contratarse de forma independiente:</Label>
                        <Select
                          value={rule.condicion_producto_id || ""}
                          onValueChange={(v) => {
                            const product = services.find(s => s.id === v);
                            setRule({ 
                              ...rule, 
                              condicion_producto_id: v || null,
                              condicion_producto_nombre: product?.nombre || null
                            });
                          }}
                        >
                          <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                            <SelectValue placeholder="Selecciona producto" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                            {services.map((s) => (
                              <SelectItem key={s.id} value={s.id} className="text-white">
                                {s.nombre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-slate-300">Mensaje de error *</Label>
                  <Textarea
                    value={rule.mensaje_error}
                    onChange={(e) => setRule({ ...rule, mensaje_error: e.target.value })}
                    placeholder="Texto que verá el comercial si viola la regla..."
                    rows={2}
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Mensaje de sugerencia (opcional)</Label>
                  <Input
                    value={rule.mensaje_sugerencia || ""}
                    onChange={(e) => setRule({ ...rule, mensaje_sugerencia: e.target.value })}
                    placeholder="Pista positiva (ej: 'Disponible porque tienes Fibra 1Gbps')"
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                </div>

                <div className="flex items-center justify-between pt-2">
                  <Label className="text-slate-300">Regla activa</Label>
                  <Switch
                    checked={rule.is_active}
                    onCheckedChange={(v) => setRule({ ...rule, is_active: v })}
                  />
                </div>

                <DialogFooter className="pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setRuleModalOpen(false);
                      setEditingRule(null);
                      resetNewRule();
                    }}
                    className="border-[#1E1E5F] text-slate-300"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={editingRule ? handleUpdateRule : handleCreateRule}
                    className="bg-[#42E695] text-[#04042C]"
                  >
                    {editingRule ? "Guardar Cambios" : "Guardar Regla"}
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
  );
};
